import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Type, Send } from "lucide-react";
import { api } from "@/lib/api";
import { STORY_GRADIENTS, DEFAULT_GRADIENT } from "@/lib/storyConfig";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function StoryComposer({ open, onClose }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [bg, setBg] = useState(DEFAULT_GRADIENT);
  const [busy, setBusy] = useState(false);

  const publish = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post("/stories", { text, background: bg });
      qc.invalidateQueries({ queryKey: ["stories"] });
      toast.success("Story shared! It disappears in 24h.");
      setText("");
      setBg(DEFAULT_GRADIENT);
      onClose();
    } catch {
      toast.error("Could not share story");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="story-composer" className="bg-card border-2 border-[#6C0BA9] rounded-3xl max-w-sm">
        <DialogTitle className="font-heading text-lg font-extrabold flex items-center gap-2">
          <Type className="w-5 h-5 text-[#C084FC]" /> Create Text Story
        </DialogTitle>

        <div
          className="relative w-full aspect-[9/16] max-h-[46vh] rounded-2xl overflow-hidden flex items-center justify-center p-6"
          style={{ background: STORY_GRADIENTS[bg] }}
        >
          <textarea
            data-testid="story-text-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={180}
            placeholder="Type something..."
            className="w-full bg-transparent text-white text-center font-heading text-2xl font-bold placeholder-white/60 resize-none outline-none"
            rows={4}
          />
        </div>

        <div className="flex items-center gap-2 justify-center flex-wrap">
          {Object.entries(STORY_GRADIENTS).map(([key, val]) => (
            <button
              key={key}
              data-testid={`story-bg-${key}`}
              onClick={() => setBg(key)}
              className={`w-9 h-9 rounded-full transition-transform ${bg === key ? "ring-2 ring-offset-2 ring-offset-card ring-white scale-110" : "hover:scale-105"}`}
              style={{ background: val }}
            />
          ))}
        </div>

        <Button data-testid="story-publish-btn" onClick={publish} disabled={busy || !text.trim()} className="w-full h-11 rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white font-semibold gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" />Share to Story</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
